import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import {
  Typography,
  Box,
  Paper,
  Chip,
  Divider,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { Alert } from '@material-ui/lab'
import { CloudUploadOutlined as CloudUploadIcon } from '@material-ui/icons'
import { Toast } from 'components/shared/Toaster'
import Loader from 'components/shared/Loader'
import Button from 'components/shared/Button'
import ReportEmptyState from 'components/shared/ReportEmptyState'
import strings from 'strings'
import BalanceSheetComparison from './BalanceSheetComparison'
import {
  BANNER_BG,
  THRESHOLDS,
  deriveBalanceCheck,
  deriveBalanceSheetVerdict,
  deriveComposition,
  deriveHealthIndicators,
  deriveTrajectory,
  formatCurrency,
  formatMultiple,
  formatPercent,
  formatPeriodLabel,
  getStatusStyle,
  readMetrics
} from './balanceSheetAdapters'
import {
  getBalanceSheetComparison,
  getBalanceSheetStatus,
  getLastBalanceSheet,
  getPreviousBalanceSheet,
  requestBalanceSheetComparison,
  uploadBalanceSheet
} from '../../../../../actions/applicants'
import CreditCost from 'components/shared/CreditCost'
import { findCreditCost, sortDocsByDate } from 'tools/helper'
import {Icon} from 'styles'
import { reportStyles } from '../shared/reportStyles'

const useStyles = makeStyles((theme) => ({
  ...reportStyles(theme),
  sidebar: {
    width: '100%'
  },
  content: {
    flex: 1,
    minWidth: 0
  },
  sidebarHeaderLeft: {
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing(1)
  },
  sidebarActions: {
    display: 'flex',
    gap: theme.spacing(1.5)
  },
  periodItem: {
    flex: '1 1 220px',
    minWidth: 200,
    border: '1px solid #ECECEC',
    borderRadius: '10px',
    padding: theme.spacing(1.5),
    cursor: 'pointer',
    transition: 'border-color 120ms ease, background-color 120ms ease'
  },
  periodItemActive: {
    borderColor: '#9b8afb',
    backgroundColor: 'rgba(155,138,251,0.06)',
    borderLeft: '3px solid #9b8afb'
  },
  railButton: {
    borderRadius: '8px',
    padding: '10px 16px',
    fontWeight: 600,
    fontSize: 13,
    border: 'none',
    whiteSpace: 'nowrap'
  },
  banner: {
    backgroundColor: BANNER_BG,
    borderRadius: '14px',
    padding: theme.spacing(4),
    color: '#fff',
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(3),
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column'
    }
  },
  bannerDescription: {
    fontSize: 14,
    lineHeight: 1.6,
    color: 'rgba(255,255,255,0.78)',
    maxWidth: 560
  },
  bannerChip: {
    fontSize: 10,
    fontWeight: 700,
    height: 20
  },
  tilesRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3)
  },
  tile: {
    flex: '1 1 180px',
    minWidth: 180,
    padding: theme.spacing(2),
    borderRadius: '12px',
    border: '1px solid #E8E8E8',
    boxShadow: 'none'
  },
  tileDark: {
    backgroundColor: BANNER_BG,
    borderColor: BANNER_BG,
    color: '#fff'
  },
  tileLabelDark: {
    color: 'rgba(255,255,255,0.6)'
  },
  tileValue: {
    fontSize: 22,
    fontWeight: 700,
    margin: '6px 0 4px'
  },
  tileNote: {
    fontSize: 11,
    color: 'var(--neutral-400)'
  },
  sectionCard: {
    padding: theme.spacing(3),
    borderRadius: '12px',
    border: '1px solid #E8E8E8',
    boxShadow: 'none'
  },
  sectionTitle: {
    fontSize: 17,
    fontWeight: 700
  },
  sectionSubtitle: {
    fontSize: 12,
    color: 'var(--neutral-400)',
    marginTop: 2
  },
  compositionRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(4),
    marginTop: theme.spacing(3)
  },
  compositionCol: {
    flex: '1 1 260px',
    minWidth: 260
  },
  columnLabel: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.08em',
    color: 'var(--neutral-400)',
    marginBottom: theme.spacing(1.5)
  },
  stackRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(3)
  },
  stack: {
    width: 56,
    height: 150,
    borderRadius: 4,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#F1F1F1',
    flexShrink: 0
  },
  legendRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 8,
    marginBottom: 6,
    fontSize: 12
  },
  legendSwatch: {
    width: 8,
    height: 8,
    borderRadius: 2,
    flexShrink: 0
  },
  legendLabel: {
    fontSize: 12,
    fontWeight: 600
  },
  legendValue: {
    fontSize: 12,
    color: '#555'
  },
  legendPct: {
    fontSize: 12,
    color: 'var(--neutral-400)'
  },
  calloutBar: {
    backgroundColor: '#F4F2FE',
    borderRadius: '10px',
    padding: theme.spacing(2),
    fontSize: 13,
    color: '#3b3b3b',
    margin: `${theme.spacing(3)}px 0`
  },
  twoColRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(3)
  },
  colNarrow: {
    flex: '1 1 260px',
    minWidth: 260
  },
  colWide: {
    flex: '3 1 420px',
    minWidth: 320
  },
  indicatorRow: {
    display: 'flex',
    gap: 10,
    marginBottom: theme.spacing(2)
  },
  indicatorDot: {
    width: 7,
    height: 7,
    borderRadius: '50%',
    marginTop: 6,
    flexShrink: 0
  },
  indicatorTitle: {
    fontSize: 13,
    fontWeight: 600
  },
  indicatorSubtext: {
    fontSize: 12,
    color: '#777',
    lineHeight: 1.5
  },
  takeawayRow: {
    display: 'flex',
    gap: theme.spacing(2),
    padding: `${theme.spacing(1.5)}px 0`,
    borderBottom: '1px solid #F0F0F0'
  },
  takeawayIndex: {
    fontSize: 11,
    fontWeight: 700,
    color: '#c4c4c4',
    marginTop: 2
  },
  takeawayText: {
    fontSize: 13,
    lineHeight: 1.6
  },
}))

const BalanceSheet = ({ applicationId, creditCosts }) => {
  const classes = useStyles()

  const [initialLoading, setInitialLoading] = useState(true)
  const [status, setStatus] = useState('NOT_STARTED')
  const [ableToCompare, setAbleToCompare] = useState(false)
  const [comparisonStatus, setComparisonStatus] = useState('NOT_STARTED')
  const [currentDoc, setCurrentDoc] = useState(null)
  const [previousDoc, setPreviousDoc] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [viewingPrevious, setViewingPrevious] = useState(false)
  const [viewMode, setViewMode] = useState('report')
  const [comparisonData, setComparisonData] = useState(null)
  const [isLoadingComparison, setIsLoadingComparison] = useState(false)
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)

  const documentComparisonCreditCost = () => findCreditCost(creditCosts, 'DOCUMENT_COMPARISON')
  const documentAnalysisCreditCost = () => findCreditCost(creditCosts, 'DOCUMENT_ANALYSIS')


  const fetchDocs = useCallback(async (hasPrevious) => {
    const current = await getLastBalanceSheet(applicationId)().catch(() => null)
    if (!hasPrevious) {
      setPreviousDoc(null)
      setCurrentDoc(current)
      return
    }
    const previous = await getPreviousBalanceSheet(applicationId)().catch(() => null)
    // Sort them by date so that current is the latest and previous is the one before
    const [sortedPrevious, sortedCurrent] = sortDocsByDate(previous, current)
    setCurrentDoc(sortedCurrent)
    setPreviousDoc(sortedPrevious)
  }, [applicationId])

  const checkStatus = useCallback(async () => {
    try {
      const res = await getBalanceSheetStatus(applicationId)()
      const nextStatus = res?.status || 'NOT_STARTED'
      const canCompareNow = !!res?.ableToCompare
      setStatus(nextStatus)
      setAbleToCompare(canCompareNow)
      setComparisonStatus(res?.comparisonStatus || 'NOT_STARTED')
      if (nextStatus === 'COMPLETED') {
        await fetchDocs(canCompareNow)
      }
      return res
    } catch (err) {
      setStatus('NOT_STARTED')
      return null
    }
  }, [applicationId, fetchDocs])

  useEffect(() => {
    if (!applicationId) {
      setInitialLoading(false)
      return
    }

    checkStatus().finally(() => setInitialLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId])

  useEffect(() => {
    if (!applicationId || status !== 'IN_PROGRESS') return

    const interval = setInterval(async () => {
      try {
        const res = await getBalanceSheetStatus(applicationId)()
        const nextStatus = res?.status || 'NOT_STARTED'
        const canCompareNow = !!res?.ableToCompare
        setStatus(nextStatus)
        setAbleToCompare(canCompareNow)
        setComparisonStatus(res?.comparisonStatus || 'NOT_STARTED')
        if (nextStatus !== 'IN_PROGRESS') {
          clearInterval(interval)
          if (nextStatus === 'COMPLETED') fetchDocs(canCompareNow)
        }
      } catch (err) {
        clearInterval(interval)
      }
    }, 3000)

    return () => clearInterval(interval)
  }, [status, applicationId, fetchDocs])

  const handleUpload = async (event) => {
    const file = event.target.files[0]
    event.target.value = ''
    if (!file || !applicationId) return

    setUploading(true)
    try {
      await uploadBalanceSheet(applicationId, file)()
      Toast({ type: 'success', message: strings.BALANCE_SHEET_UPLOAD_SUCCESS })
      await checkStatus()
    } catch (err) {
      Toast({ type: 'error', message: strings.BALANCE_SHEET_UPLOAD_ERROR })
    } finally {
      setUploading(false)
    }
  }

  const canCompare = !!(currentDoc && previousDoc) && ableToCompare


  const handleCancelComparison = () => {
    setShowConfirmDialog(false)
  }

  const handleRequestComparison = async () => {
    if (!applicationId) return
    setShowConfirmDialog(false)
    setViewMode('comparison')
    if (comparisonData) return
    setIsLoadingComparison(true)
    try {
      // Read an already generated comparison when the status says there is one;
      // that GET is silent on failure, so an unimplemented route falls through
      // to the POST that generates it.
      let res = null
      if (comparisonStatus === 'COMPLETED') {
        res = await getBalanceSheetComparison(applicationId)().catch(() => null)
      }
      if (!res) {
        res = await requestBalanceSheetComparison(applicationId)()
      }
      setComparisonData(res)
    } catch (err) {
      Toast({ type: 'error', message: strings.BALANCE_SHEET_COMPARISON_FAILED })
      setViewMode('report')
    } finally {
      setIsLoadingComparison(false)
    }
  }


  const handleClickComparison = () => {
    if (!canCompare) return
    if (isLoadingComparison) return

    if (comparisonStatus === 'NOT_STARTED' && !comparisonData) {
      setViewMode('report')
      setShowConfirmDialog(true)
    } else {
      handleRequestComparison()
    }
  }

  const activeDoc = viewingPrevious ? previousDoc : currentDoc
  const otherDoc = viewingPrevious ? currentDoc : previousDoc

  const verdict = useMemo(
    () => (activeDoc ? deriveBalanceSheetVerdict(activeDoc, otherDoc) : null),
    [activeDoc, otherDoc]
  )
  const metrics = verdict ? verdict.metrics : null
  const statusStyle = getStatusStyle(verdict ? verdict.statusLabel : null)
  const composition = useMemo(() => (metrics ? deriveComposition(metrics) : null), [metrics])
  const indicators = useMemo(() => (metrics ? deriveHealthIndicators(metrics) : []), [metrics])
  const balanceCheck = useMemo(() => (metrics ? deriveBalanceCheck(metrics) : null), [metrics])
  const trajectory = useMemo(() => (metrics ? deriveTrajectory(metrics) : null), [metrics])

  if (initialLoading) {
    return (
      <Box className={classes.container}>
        <Loader />
      </Box>
    )
  }

  if (uploading || status === 'IN_PROGRESS') {
    return (
      <Box className={classes.container}>
        <Box className={classes.centeredState}>
          <CircularProgress size={48} thickness={4} style={{ color: BANNER_BG, marginBottom: 24 }} />
          <Typography variant="h5" style={{ marginBottom: 16, fontWeight: 600 }}>
            {strings.BALANCE_SHEET_PROCESSING_TITLE}
          </Typography>
          <Typography variant="body1" style={{ color: '#666' }}>
            {strings.BALANCE_SHEET_PROCESSING_MESSAGE}
          </Typography>
        </Box>
      </Box>
    )
  }

  if (!currentDoc) {
    return (
      <Box className={classes.container}>
        <input
          accept=".pdf,.jpg,.jpeg,.png,.csv,.xls,.xlsx"
          style={{ display: 'none' }}
          id="balance-sheet-upload-empty"
          type="file"
          onChange={handleUpload}
        />
        <ReportEmptyState
          title={strings.BALANCE_SHEET_NO_REPORTS_TITLE}
          description={strings.BALANCE_SHEET_NO_REPORTS_MESSAGE}
          action={
            <Button
              size="large"
              style={{ padding: '12px 32px', fontSize: '1rem' }}
              loading={uploading}
              onClick={() => document.getElementById('balance-sheet-upload-empty')?.click()}
            >
              {strings.BALANCE_SHEET_UPLOAD_DOCUMENT} <CreditCost cost={documentAnalysisCreditCost()} />
            </Button>
          }
        />
      </Box>
    )
  }

  const rail = (
    <BalanceSheetRail
      classes={classes}
      currentDoc={currentDoc}
      previousDoc={previousDoc}
      viewingPrevious={viewingPrevious}
      onSelect={(isPrevious) => { setViewingPrevious(isPrevious); setViewMode('report') }}
      canCompare={canCompare}
      onCompareClick={handleClickComparison}
      onUpload={handleUpload}
      uploading={uploading}
      creditCosts={creditCosts}
    />
  )

  if (viewMode === 'comparison') {
    return (
      <div className={classes.container}>
        <div className={classes.layout}>
          {rail}
          <div className={classes.content}>
            {isLoadingComparison ? (
              <Box className={classes.centeredState}>
                <CircularProgress size={48} thickness={4} style={{ color: BANNER_BG, marginBottom: 24 }} />
                <Typography variant="h5" style={{ marginBottom: 16, fontWeight: 600 }}>
                  {strings.BALANCE_SHEET_GENERATING_COMPARISON_TITLE}
                </Typography>
                <Typography variant="body1" style={{ color: '#666' }}>
                  {strings.BALANCE_SHEET_ANALYZING_CHANGES}
                </Typography>
              </Box>
            ) : (
              <BalanceSheetComparison
                fromDoc={previousDoc}
                toDoc={currentDoc}
                comparisonData={comparisonData}
                onBack={() => setViewMode('report')}
              />
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={classes.container}>
      <div className={classes.layout}>
        {rail}

        <div className={classes.content}>
          {metrics.error && (
            <Alert severity="warning" style={{ marginBottom: 16 }}>
              {metrics.error}
            </Alert>
          )}

          {/* Verdict banner */}
          <div className={classes.banner}>
            <div>
              <Typography className={classes.bannerEyebrow} style={{ color: statusStyle.accent }}>
                {verdict.eyebrow}
              </Typography>
              <Typography className={classes.bannerHeadline}>{verdict.headline}</Typography>
              <Typography className={classes.bannerDescription}>{verdict.description}</Typography>
            </div>
            <div className={classes.bannerScoreCol}>
              <Typography className={classes.bannerScoreLabel}>{strings.BALANCE_SHEET_HEALTH_SCORE}</Typography>
              <Typography className={classes.bannerScoreValue} style={{ color: statusStyle.accent }}>
                {metrics.healthScore.toFixed(1)}<span className={classes.bannerScoreUnit}>/10</span>
              </Typography>
              <Chip
                label={verdict.statusLabel}
                size="small"
                className={classes.bannerChip}
                style={{ backgroundColor: statusStyle.chipBg, color: statusStyle.chipText }}
              />
              <div className={classes.dotStrip}>
                {Array.from({ length: 10 }).map((_, i) => (
                  <div
                    key={i}
                    className={classes.dot}
                    style={i < Math.round(metrics.healthScore) ? { backgroundColor: statusStyle.accent } : undefined}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Headline figures */}
          <div className={classes.tilesRow}>
            <Paper className={classes.tile}>
              <Typography className={classes.tileLabel}>{strings.BALANCE_SHEET_TOTAL_ASSETS}</Typography>
              <Typography className={classes.tileValue}>{formatCurrency(metrics.totalAssets)}</Typography>
              <Typography className={classes.tileNote}>{verdict.tileNotes.assets}</Typography>
            </Paper>
            <Paper className={classes.tile}>
              <Typography className={classes.tileLabel}>{strings.BALANCE_SHEET_TOTAL_LIABILITIES}</Typography>
              <Typography className={classes.tileValue}>{formatCurrency(metrics.totalLiabilities)}</Typography>
              <Typography className={classes.tileNote}>{verdict.tileNotes.liabilities}</Typography>
            </Paper>
            <Paper
              className={classes.tile}
              style={metrics.workingCapital < 0 ? { borderColor: statusStyle.accent } : undefined}
            >
              <Typography className={classes.tileLabel}>{strings.BALANCE_SHEET_WORKING_CAPITAL}</Typography>
              <Typography
                className={classes.tileValue}
                style={{ color: metrics.workingCapital >= 0 ? 'var(--status-approved)' : 'var(--status-pending)' }}
              >
                {metrics.workingCapital >= 0 ? '+' : ''}{formatCurrency(metrics.workingCapital)}
              </Typography>
              <Typography
                className={classes.tileNote}
                style={metrics.workingCapital < 0 ? { color: statusStyle.accent } : undefined}
              >
                {verdict.tileNotes.workingCapital}
              </Typography>
            </Paper>
            <Paper
              className={classes.tile}
              style={metrics.debtToEquityRatio > THRESHOLDS.DEBT_TO_EQUITY ? { borderColor: '#9b8afb' } : undefined}
            >
              <Typography className={classes.tileLabel}>{strings.BALANCE_SHEET_DEBT_TO_EQUITY}</Typography>
              <Typography className={classes.tileValue}>{formatMultiple(metrics.debtToEquityRatio)}</Typography>
              <Typography className={classes.tileNote}>{verdict.tileNotes.leverage}</Typography>
            </Paper>
            <Paper className={`${classes.tile} ${classes.tileDark}`}>
              <Typography className={`${classes.tileLabel} ${classes.tileLabelDark}`}>{strings.BALANCE_SHEET_TOTAL_EQUITY}</Typography>
              <Typography className={classes.tileValue}>{formatCurrency(metrics.totalEquity)}</Typography>
              <Typography className={classes.tileNote} style={{ color: metrics.totalEquity > 0 ? '#7fd9ab' : '#f0a988' }}>
                {verdict.tileNotes.equity}
              </Typography>
            </Paper>
          </div>

          {/* Composition */}
          <Paper className={classes.sectionCard}>
            <Typography className={classes.sectionTitle}>{strings.BALANCE_SHEET_COMPOSITION}</Typography>
            <Typography className={classes.sectionSubtitle}>{balanceCheck.text}</Typography>

            <div className={classes.compositionRow}>
              <CompositionColumn
                classes={classes}
                label={strings.BALANCE_SHEET_ASSETS_COLUMN}
                column={composition.assets}
              />
              <CompositionColumn
                classes={classes}
                label={strings.BALANCE_SHEET_CLAIMS_COLUMN}
                column={composition.claims}
              />
            </div>
          </Paper>

          {trajectory && (
            <div className={classes.calloutBar}>
              <strong>{strings.BALANCE_SHEET_TRAJECTORY}</strong> {trajectory}
            </div>
          )}

          <div className={classes.twoColRow} style={trajectory ? undefined : { marginTop: 24 }}>
            {/* Derived signals */}
            <div className={classes.colNarrow}>
              <Paper className={classes.sectionCard}>
                <Typography className={classes.sectionTitle}>{strings.BALANCE_SHEET_HEALTH_INDICATORS}</Typography>
                <Typography className={classes.sectionSubtitle}>
                  {indicators.length} {strings.BALANCE_SHEET_INDICATORS_SUBTITLE_SUFFIX}
                </Typography>
                <Box mt={2}>
                  {indicators.map((indicator) => (
                    <div className={classes.indicatorRow} key={indicator.title}>
                      <div
                        className={classes.indicatorDot}
                        style={{ backgroundColor: indicator.warning ? 'var(--status-pending)' : '#2c8f68' }}
                      />
                      <div>
                        <Typography className={classes.indicatorTitle}>{indicator.title}</Typography>
                        <Typography className={classes.indicatorSubtext}>{indicator.subtext}</Typography>
                      </div>
                    </div>
                  ))}
                </Box>
                <Divider style={{ margin: '16px 0' }} />
                <Typography className={classes.tileLabel}>{strings.BALANCE_SHEET_TOTAL_DEBT}</Typography>
                <Typography style={{ fontSize: 20, fontWeight: 700, marginTop: 4 }}>
                  {formatCurrency(metrics.totalDebt)}
                </Typography>
                <Typography className={classes.tileNote}>
                  {formatMultiple(metrics.debtToEquityRatio)} {strings.BALANCE_SHEET_DEBT_TO_EQUITY_LABEL.toLowerCase()}
                </Typography>
              </Paper>
            </div>

            {/* Takeaways */}
            <div className={classes.colWide}>
              <Paper className={classes.sectionCard}>
                <Typography className={classes.sectionTitle}>{strings.BALANCE_SHEET_TAKEAWAYS}</Typography>
                <Typography className={classes.sectionSubtitle}>{strings.BALANCE_SHEET_TAKEAWAYS_SUBTITLE}</Typography>
                <Box mt={2}>
                  {metrics.takeaways.map((takeaway, index) => (
                    <div className={classes.takeawayRow} key={takeaway}>
                      <Typography className={classes.takeawayIndex}>{String(index + 1).padStart(2, '0')}</Typography>
                      <Typography className={classes.takeawayText}>{takeaway}</Typography>
                    </div>
                  ))}
                </Box>
              </Paper>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Dialog for Comparison */}
      <Dialog
        open={showConfirmDialog}
        onClose={handleCancelComparison}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {strings.BALANCE_SHEET_GENERATE_COMPARISON_TITLE}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            {strings.BALANCE_SHEET_GENERATE_COMPARISON_MESSAGE}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleCancelComparison}
            variant="outlined"
            color="secondary"
          >
            {strings.CANCEL}
          </Button>
          <Tooltip
            title={""}
            arrow
            placement="top"
          >
            <span>
              <Button
                onClick={handleRequestComparison}
                variant="contained"
                color="primary"
                disabled={false}
              >
                {strings.BALANCE_SHEET_GENERATE_COMPARISON_TITLE}
                <CreditCost cost={documentComparisonCreditCost()} />
              </Button>
            </span>
          </Tooltip>
        </DialogActions>
      </Dialog>
    </div>
  )
}

const CompositionColumn = ({ classes, label, column }) => (
  <div className={classes.compositionCol}>
    <Typography className={classes.columnLabel}>{label}</Typography>
    <div className={classes.stackRow}>
      <div className={classes.stack}>
        {column.segments.map((segment) => (
          <div
            key={segment.key}
            style={{ height: `${segment.pct}%`, backgroundColor: segment.color }}
          />
        ))}
      </div>
      <div>
        {column.segments.map((segment) => (
          <div className={classes.legendRow} key={segment.key}>
            <span className={classes.legendSwatch} style={{ backgroundColor: segment.color }} />
            <span className={classes.legendLabel}>{segment.label}</span>
            <span className={classes.legendValue}>{formatCurrency(segment.value)}</span>
            <span className={classes.legendPct}>{formatPercent(segment.sharePct)}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
)

const BalanceSheetRail = ({
  classes,
  currentDoc,
  previousDoc,
  viewingPrevious,
  onSelect,
  canCompare,
  onCompareClick,
  onUpload,
  uploading,
  creditCosts
}) => {
  const periodsCount = previousDoc ? 2 : 1
  const documentAnalysisCreditCost = () => findCreditCost(creditCosts, 'DOCUMENT_ANALYSIS')

  const renderCard = (doc, isPrevious) => {
    const metrics = readMetrics(doc)
    const statusLabel = deriveBalanceSheetVerdict(doc, null).statusLabel
    const statusStyle = getStatusStyle(statusLabel)
    const isActive = viewingPrevious === isPrevious


    return (
      <div
        className={`${classes.periodItem} ${isActive ? classes.periodItemActive : ''}`}
        onClick={() => onSelect(isPrevious)}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography className={classes.periodDate}>
            {isPrevious ? strings.BALANCE_SHEET_PREVIOUS : strings.BALANCE_SHEET_CURRENT} · {formatPeriodLabel(metrics.date)}
          </Typography>
          <Typography className={classes.periodStatus} style={{ color: statusStyle.accent }}>{statusLabel}</Typography>
        </Box>
        <Typography className={classes.periodValue}>{formatCurrency(metrics.totalAssets)}</Typography>
        <Typography className={classes.periodMeta}>{strings.BALANCE_SHEET_TOTAL_ASSETS_SHORT}</Typography>
        <Typography className={classes.periodMeta}>
          {strings.BALANCE_SHEET_HEALTH} {metrics.healthScore.toFixed(1)} / 10 · D/E {formatMultiple(metrics.debtToEquityRatio)}
        </Typography>
        <div className={classes.periodBar}>
          <div
            className={classes.periodBarFill}
            style={{ width: `${Math.min(100, (metrics.healthScore / 10) * 100)}%`, backgroundColor: statusStyle.accent }}
          />
        </div>
      </div>
    )
  }

  return (
    <div className={classes.sidebar}>
      <Paper className={classes.sidebarCard}>
        <div className={classes.sidebarHeader}>
          <div className={classes.sidebarHeaderLeft}>
            <Typography style={{ fontWeight: 700, fontSize: 16 }}>{strings.BALANCE_SHEET_STATEMENTS}</Typography>
            <Typography className={classes.periodMeta}>{periodsCount} {strings.BALANCE_SHEET_PERIODS_SUFFIX}</Typography>
          </div>
          <div className={classes.sidebarActions}>
            <button
              type="button"
              className={classes.railButton}
              disabled={!canCompare}
              onClick={onCompareClick}
              style={{
                backgroundColor: canCompare ? '#9b8afb' : '#EDEBFB',
                color: canCompare ? '#fff' : '#b3a9f5',
                cursor: canCompare ? 'pointer' : 'not-allowed'
              }}
            >
              {strings.BALANCE_SHEET_COMPARE_BUTTON}
            </button>

            <input
              accept=".pdf,.jpg,.jpeg,.png,.csv,.xls,.xlsx"
              style={{ display: 'none' }}
              id="balance-sheet-upload"
              type="file"
              onChange={onUpload}
            />
            <label htmlFor="balance-sheet-upload">
              <span
                role="button"
                tabIndex={0}
                className={classes.railButton}
                style={{
                  backgroundColor: '#fff',
                  color: '#333',
                  border: '1px solid #ddd',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                  cursor: uploading ? 'wait' : 'pointer',
                  opacity: uploading ? 0.6 : 1
                }}
              >
                <CloudUploadIcon style={{ fontSize: 16 }} />
                {uploading ? strings.BALANCE_SHEET_UPLOADING : strings.BALANCE_SHEET_UPLOAD_BUTTON} <CreditCost cost={documentAnalysisCreditCost()} />
              </span>
            </label>
          </div>
        </div>

        <div className={classes.periodsRow}>
          {previousDoc && renderCard(previousDoc, true)}
          {renderCard(currentDoc, false)}
        </div>
      </Paper>
    </div>
  )
}

BalanceSheet.propTypes = {
  applicationId: PropTypes.string
}

CompositionColumn.propTypes = {
  classes: PropTypes.object.isRequired,
  label: PropTypes.string.isRequired,
  column: PropTypes.object.isRequired
}

BalanceSheetRail.propTypes = {
  classes: PropTypes.object.isRequired,
  currentDoc: PropTypes.object.isRequired,
  previousDoc: PropTypes.object,
  viewingPrevious: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
  canCompare: PropTypes.bool.isRequired,
  onCompareClick: PropTypes.func.isRequired,
  onUpload: PropTypes.func.isRequired,
  uploading: PropTypes.bool.isRequired,
  creditCosts: PropTypes.array.isRequired
}

export default BalanceSheet
